/**
 * cloud-api 호출 클라이언트.
 *
 * - baseURL 은 dev 에서 http://localhost:6100 (CLAUDE.md), prod 에선 환경변수
 *   `VITE_API_BASE_URL` 로 주입. 빌드 시점에 결정.
 * - 토큰은 localStorage 에 저장 (웹 컨텍스트 — keytar 없음). 보안 트레이드오프:
 *   XSS 위험 vs 사용성. 향후 cookie + httpOnly + secure 로 이전 검토.
 * - 401 응답 시 refresh 시도 (1회), 재실패 시 토큰 폐기 + 로그인 페이지로.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

const API_BASE_URL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ??
  "http://localhost:6100/api";

const ACCESS_TOKEN_KEY = "devit.web.accessToken";
const REFRESH_TOKEN_KEY = "devit.web.refreshToken";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export function loadTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

export function saveTokens(tokens: AuthTokens): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const tokens = loadTokens();
  if (tokens?.accessToken) {
    config.headers.set("Authorization", `Bearer ${tokens.accessToken}`);
  }
  return config;
});

let refreshing: Promise<AuthTokens | null> | null = null;

async function tryRefresh(): Promise<AuthTokens | null> {
  if (refreshing) return refreshing;
  const tokens = loadTokens();
  if (!tokens?.refreshToken) return null;
  refreshing = (async () => {
    try {
      // refresh 는 새 axios 인스턴스로 — interceptor 무한 루프 방지.
      const { data } = await axios.post<AuthTokens>(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken: tokens.refreshToken },
        { timeout: 10_000 },
      );
      saveTokens(data);
      return data;
    } catch {
      clearTokens();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

apiClient.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (err.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      const fresh = await tryRefresh();
      if (fresh) {
        original.headers.set("Authorization", `Bearer ${fresh.accessToken}`);
        return apiClient(original);
      }
    }
    return Promise.reject(err);
  },
);

export async function verifyEmail(token: string): Promise<void> {
  await apiClient.post("/auth/verify-email", { token });
}

export async function resendVerification(): Promise<void> {
  await apiClient.post("/auth/resend-verification");
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post("/auth/forgot-password", { email });
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post("/auth/reset-password", { token, newPassword });
}

export function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join(", ") : data.message;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
